/* eslint-disable max-lines */
import {
  Situation,
  SOPTalkdown,
  SOPTalkdownType,
  SOPWorkflow,
} from '@hakimo-ui/hakimo/types';
import { Label } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ReactNode } from 'react';
import { TalkdownMode } from '../audio-recorder/util';
import { SituationChips, SOPItemList } from '../sop-workflow/utils';

dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

export enum SOPTabId {
  SITUATIONS = 'situations',
  TALKDOWN_NOTES = 'talkdownNotes',
  ESCALATION_PROTOCOL = 'escalationProtocol',
}

export enum SOPCardId {
  SITUATIONS = 'v2.situations',
  EXCEPTIONS = 'v2.exceptions',
  NOTES = 'v2.notes',
  TALKDOWN_NOTES = 'v2.talkdownNotes',
  ESCALATION_PROTOCOL = 'v2.escalationProtocol',
  ESCALATION_POINTS = 'v2.escalationPoints',
}

export interface SOPCardDef {
  id: SOPCardId;
  tab: SOPTabId;
  title: string;
  content: ReactNode;
  isEmpty: boolean;
}

export interface SOPFreshness {
  isRecent: boolean;
  label: string;
}

export const MUTED_ICON_CLASS =
  'text-onlight-text-2 dark:text-ondark-text-2 h-4 w-4 flex-shrink-0';

export const FIELD_CLASS =
  'border-onlight-line-3 dark:border-ondark-line-2 bg-onlight-bg-1 dark:bg-ondark-bg-1 text-onlight-text-1 dark:text-ondark-text-1 placeholder:text-onlight-text-3 dark:placeholder:text-ondark-text-3 focus:border-primary-500 focus:ring-primary-500 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1';

export const SOP_RECENT_EDIT_DAYS = 15;

const HOURS_PER_DAY = 24;
const HOURS_PER_MONTH = 30 * HOURS_PER_DAY;
const HOURS_PER_YEAR = 365 * HOURS_PER_DAY;

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

function elapsedLabel(hours: number): string {
  if (hours < 1) {
    return 'less than an hour ago';
  }
  if (hours < HOURS_PER_DAY) {
    return `${plural(hours, 'hour')} ago`;
  }
  if (hours < HOURS_PER_MONTH) {
    return `${plural(Math.floor(hours / HOURS_PER_DAY), 'day')} ago`;
  }
  if (hours < HOURS_PER_YEAR) {
    return `${plural(Math.floor(hours / HOURS_PER_MONTH), 'month')} ago`;
  }
  return `${plural(Math.floor(hours / HOURS_PER_YEAR), 'year')} ago`;
}

export function getSOPFreshness(updatedAtIso?: string): SOPFreshness {
  if (!updatedAtIso) {
    return { isRecent: false, label: 'Last edited: unknown' };
  }

  const editedAt = dayjs.utc(updatedAtIso);
  if (!editedAt.isValid()) {
    return { isRecent: false, label: 'Last edited: unknown' };
  }

  const hoursSinceEdit = dayjs.utc().diff(editedAt, 'hour');

  return {
    isRecent: hoursSinceEdit <= SOP_RECENT_EDIT_DAYS * 24,
    label: `Last edited: ${elapsedLabel(hoursSinceEdit)}`,
  };
}

function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <ul className="space-y-2 text-sm">
      {items.map((item, i) => (
        <li key={i} className="[overflow-wrap:anywhere]">
          {'•'} {item}
        </li>
      ))}
    </ul>
  );
}

function PlainList({ items }: { items: string[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <ul className="space-y-1.5 text-sm">
      {items.map((item, i) => (
        <li key={i} className="[overflow-wrap:anywhere]">
          {item}
        </li>
      ))}
    </ul>
  );
}

export interface TalkdownStep {
  index: number;
  mode: TalkdownMode;
  label: string;
  text: string;
}

const TALKDOWN_MODE_LABEL: Record<TalkdownMode, string> = {
  [TalkdownMode.STATIC]: 'Static',
  [TalkdownMode.DYNAMIC]: 'Dynamic',
  [TalkdownMode.CUSTOM]: 'Custom',
  [TalkdownMode.RECORD]: 'Record',
};

function isTalkdownMode(value: string): value is TalkdownMode {
  return (Object.values(TalkdownMode) as string[]).includes(value);
}

export function talkdownModeFromSOPType(type: SOPTalkdownType): TalkdownMode {
  return isTalkdownMode(type) ? type : TalkdownMode.STATIC;
}

export function talkdownTypeLabel(type: SOPTalkdownType): string {
  return TALKDOWN_MODE_LABEL[talkdownModeFromSOPType(type)];
}

export function buildTalkdownSteps(talkdowns?: SOPTalkdown[]): TalkdownStep[] {
  return (talkdowns ?? []).map((talkdown, i) => {
    const mode = talkdownModeFromSOPType(talkdown.type);
    return {
      index: i + 1,
      mode,
      label: `${TALKDOWN_MODE_LABEL[mode]} talkdown`,
      text: talkdown.text,
    };
  });
}

interface TalkdownNotesProps {
  scripts: SOPTalkdown[];
  notes: string[];
}

const SOP_SUB_HEADING =
  'text-onlight-text-1 dark:text-ondark-text-1 font-medium';

function TalkdownNotes({ scripts, notes }: TalkdownNotesProps) {
  return (
    <div className="space-y-6 text-sm">
      {scripts.map((script, i) => (
        <div key={i}>
          <div className="mb-2 flex items-center justify-start">
            <span className={SOP_SUB_HEADING}>Talkdown {i + 1}</span>
            <span className="ml-2">
              <Label small text={talkdownTypeLabel(script.type)} />
            </span>
          </div>
          <p className="text-onlight-text-2 dark:text-ondark-text-2 whitespace-pre-wrap">
            {script.text}
          </p>
        </div>
      ))}

      {notes.length > 0 && (
        <div className="space-y-2">
          <h4 className={SOP_SUB_HEADING}>Notes</h4>
          <ol className="list-inside list-decimal space-y-1">
            {notes.map((note, i) => (
              <li
                key={i}
                className="text-onlight-text-2 dark:text-ondark-text-2"
              >
                <span className="break-words">{note}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

interface BuildSOPCardsContext {
  onSituationSelect?: (situation: Situation) => void;
  alarmStartEpochMs?: number;
  alarmEndEpochMs?: number;
  locationTimezone?: string;
}

export function buildSOPCards(
  sop: SOPWorkflow | undefined,
  ctx: BuildSOPCardsContext = {}
): SOPCardDef[] {
  const situations = sop?.situations ?? [];
  const exceptions = sop?.exceptions ?? [];
  const notes = sop?.notes ?? [];
  const talkdownNotes = sop?.talkdownNotes ?? [];
  const talkdownScripts = sop?.talkdowns ?? [];
  const escalationProtocol = sop?.escalationProtocol ?? [];
  const escalationPoints = sop?.escalationPoints ?? [];

  return [
    {
      id: SOPCardId.SITUATIONS,
      tab: SOPTabId.SITUATIONS,
      title: 'Situations',
      isEmpty: situations.length === 0,
      content: (
        <SituationChips
          variant="filled"
          situations={situations}
          onSelect={ctx.onSituationSelect}
          alarmStartEpochMs={ctx.alarmStartEpochMs}
          alarmEndEpochMs={ctx.alarmEndEpochMs}
          locationTimezone={ctx.locationTimezone}
        />
      ),
    },
    {
      id: SOPCardId.EXCEPTIONS,
      tab: SOPTabId.SITUATIONS,
      title: 'Exceptions',
      isEmpty: exceptions.length === 0,
      content: (
        <div className="text-sm">
          <SOPItemList items={exceptions} />
        </div>
      ),
    },
    {
      id: SOPCardId.NOTES,
      tab: SOPTabId.SITUATIONS,
      title: 'Notes',
      isEmpty: notes.length === 0,
      content: (
        <div className="text-sm">
          <SOPItemList items={notes} />
        </div>
      ),
    },
    {
      id: SOPCardId.TALKDOWN_NOTES,
      tab: SOPTabId.TALKDOWN_NOTES,
      title: 'Talkdown Notes',
      isEmpty: talkdownNotes.length === 0 && talkdownScripts.length === 0,
      content: (
        <TalkdownNotes scripts={talkdownScripts} notes={talkdownNotes} />
      ),
    },
    {
      id: SOPCardId.ESCALATION_PROTOCOL,
      tab: SOPTabId.ESCALATION_PROTOCOL,
      title: 'Escalation Protocol',
      isEmpty: escalationProtocol.length === 0 && escalationPoints.length === 0,
      content: <BulletList items={escalationProtocol} />,
    },
    {
      id: SOPCardId.ESCALATION_POINTS,
      tab: SOPTabId.ESCALATION_PROTOCOL,
      title: 'Points to Remember',
      isEmpty: escalationPoints.length === 0,
      content: <PlainList items={escalationPoints} />,
    },
  ];
}

const DETACH_OFFSET_X = 420;
const DETACH_CASCADE = 28;
const VIEWPORT_MARGIN = 16;

export const CARD_ROOT_CLASS = 'min-w-0 rounded-lg';

export const HEADER_ICON_CLASS =
  'text-onlight-text-2 dark:text-ondark-text-2 hover:text-onlight-text-1 dark:hover:text-ondark-text-1';

const ICON_HOVER_ON_BG_1 =
  'hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3' as const;
const ICON_HOVER_ON_BG_3 =
  'hover:bg-onlight-line-3 dark:hover:bg-ondark-bg-3' as const;

export const CARD_VARIANTS = {
  section: {
    headerRule: '',
    titleGroup: 'gap-1.5',
    root: 'dark:bg-ondark-bg-1 bg-onlight-bg-1',
    padding: 'p-2.5',
    header: 'items-center',
    title: 'font-semibold',
    body: 'pt-2',
    actions: '',
    iconButton: '!p-1',
    iconHover: ICON_HOVER_ON_BG_1,
  },
  panel: {
    headerRule: '',
    titleGroup: 'gap-1.5',
    root: 'dark:bg-ondark-bg-1 bg-onlight-bg-1',
    padding: 'p-3',
    header: 'items-center',
    title: 'font-semibold',
    body: 'pt-2',
    actions: '',
    iconButton: '!p-1',
    iconHover: ICON_HOVER_ON_BG_1,
  },
  surface: {
    headerRule: '',
    titleGroup: 'gap-2',
    root: 'dark:bg-hakimo-dark-bg bg-onlight-bg-3',
    padding: 'p-4',
    header: 'items-center',
    title: 'font-bold',
    body: 'pt-3',
    actions: 'gap-2',
    iconButton: '!p-1',
    iconHover: ICON_HOVER_ON_BG_3,
  },
  shell: {
    titleGroup: 'gap-1',
    root: 'dark:bg-hakimo-dark-bg bg-onlight-bg-3 overflow-hidden',
    padding: '',
    header: 'items-center p-4',
    headerRule: 'border-b border-onlight-line-3 dark:border-ondark-line-2',
    title: 'font-bold',
    body: 'p-2.5',
    actions: 'gap-2',
    iconButton: '!p-0',
    iconHover: ICON_HOVER_ON_BG_3,
  },
};

export function getDetachPosition(rect: DOMRect, alreadyDetached: number) {
  return {
    top: Math.min(
      Math.max(VIEWPORT_MARGIN, rect.top + alreadyDetached * DETACH_CASCADE),
      Math.max(VIEWPORT_MARGIN, window.innerHeight - 120)
    ),
    left: Math.max(
      VIEWPORT_MARGIN,
      rect.left - DETACH_OFFSET_X - alreadyDetached * DETACH_CASCADE
    ),
  };
}

export const FIELD_LABEL =
  'text-onlight-text-1 dark:text-ondark-text-1 text-xs';

const MESSAGE_BOX_SHAPE =
  'rounded-xl text-sm leading-relaxed [overflow-wrap:anywhere]';

export const STATIC_MESSAGE_BOX = `border-primary-500/60 dark:bg-primary-900/20 bg-primary-50 border p-2 ${MESSAGE_BOX_SHAPE}`;
export const DYNAMIC_MESSAGE_BOX = `bg-onlight-bg-1 dark:bg-ondark-bg-1 text-onlight-text-1 dark:text-ondark-text-1 p-4 ${MESSAGE_BOX_SHAPE}`;

export const ICON_BUTTON_CLASS = clsx(
  '!p-1',
  HEADER_ICON_CLASS,
  'hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3'
);
